import React from "react";
import BaseModal from "@reusables/Modals/BaseModal";
import { Trans, useTranslation } from "react-i18next";
import BaseButton from "@reusables/BaseButton";
import { ReactComponent as CustomerIcon } from "@assets/icons/sidebar/ic_person.svg";
import { ReactComponent as SuppliersIcon } from "@assets/icons/sidebar/ic_package_delivery.svg";
import { ReactComponent as AllProductsIcon } from "@assets/icons/sidebar/ic_small_bag.svg";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { Alert, Collapse } from "@mui/material";
import { useImportPOGODataMutation } from "@redux/features/integrations/integrationsApi";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import BaseDropdown from "@reusables/dropdowns/BaseDropdown";
import { useGetModuleLocationsQuery } from "@redux/features/locations/locationsApi";
import { isDataTransferStateActive } from "@redux/features/dfs/types";
import { selectOperation } from "@redux/features/dfs/dfsSlice";
import { performImport } from "@redux/features/dfs/thunks";

interface PowerofficeImportModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const formSchema = z.object({
    location: z.object({
        store: z.object({
            id: z.number(),
            name: z.string(),
        }),
        section: z
            .object({
                id: z.number(),
                name: z.string(),
            })
            .nullish(),
    }),
});

type FormTyping = z.infer<typeof formSchema>;

export default function PowerofficeImportModal({ isOpen, onClose }: PowerofficeImportModalProps) {
    const { t } = useTranslation("", {
        keyPrefix: "imports.modules.poweroffice.importModal",
    });

    const dispatch = useAppDispatch();

    const [importPOGO, { isLoading: isImportLoading }] = useImportPOGODataMutation();

    const isImportInProgress = useAppSelector((state) =>
        isDataTransferStateActive(selectOperation("import")(state))
    );

    const { data: locationsOptions, isLoading: isLocationsOptionsLoading } =
        useGetModuleLocationsQuery("integrations");

    const form = useForm<FormTyping>({
        resolver: zodResolver(formSchema),
    });

    const onSubmit = form.handleSubmit((data) => {
        dispatch(
            performImport("poweroffice", () => {
                return importPOGO({
                    store_id: data.location.store.id,
                    section_id: data.location.section?.id,
                }).unwrap();
            })
        );

        form.reset();
        onClose();
    });

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={onClose}
            width={800}
            padding="45px"
            useCloseIcon
            isLoading={isImportLoading}
        >
            <form onSubmit={onSubmit}>
                <div className="text-xl font-semibold text-center text-accent mb-4">
                    {t("heading")}
                </div>

                <Collapse in={isImportInProgress}>
                    <Alert severity={"warning"} className={"mb-4"}>
                        {t("importIsAlreadyInProgressAlert")}
                    </Alert>
                </Collapse>

                {/* What we import */}
                <div className={"space-y-2 mb-4"}>
                    <div className="font-semibold text-inputs-label-dim">
                        {t("whatWeImport.label")}
                    </div>
                    <div className={"grid grid-cols-3 gap-2 text-accent"}>
                        <div
                            className={
                                "flex flex-row justify-between border-[1px] border-solid border-inputs-border-default rounded-[8px] p-[16px]"
                            }
                        >
                            <div>{t("whatWeImport.products")}</div>
                            <div>
                                <AllProductsIcon width={24} height={24} />
                            </div>
                        </div>
                        <div
                            className={
                                "flex flex-row justify-between border-[1px] border-solid border-inputs-border-default rounded-[8px] p-[16px]"
                            }
                        >
                            <div>{t("whatWeImport.customers")}</div>
                            <div>
                                <CustomerIcon width={24} height={24} />
                            </div>
                        </div>
                        <div
                            className={
                                "flex flex-row justify-between border-[1px] border-solid border-inputs-border-default rounded-[8px] p-[16px]"
                            }
                        >
                            <div>{t("whatWeImport.suppliers")}</div>
                            <div>
                                <SuppliersIcon width={24} height={24} />
                            </div>
                        </div>
                    </div>
                </div>
                <div className={"space-y-2 mb-4"}>
                    <div className="font-semibold text-inputs-label-dim">{t("rules.label")}:</div>
                    <ul className={"list-disc ml-4 text-accent"}>
                        <li>{t("rules.sync")};</li>
                        <li>
                            <Trans
                                t={t}
                                i18nKey="rules.masterStock"
                                components={{ b: <strong /> }}
                            />
                        </li>
                    </ul>
                </div>

                <div>
                    <Controller
                        name={"location"}
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <BaseDropdown
                                {...field}
                                error={fieldState.error}
                                label={t("locationDropdown.label") + " *"}
                                placeholder={t("locationDropdown.placeholder")}
                                options={locationsOptions}
                                getter={{
                                    label: (opt) =>
                                        `${opt.store.name}${
                                            opt.section ? ` - ${opt.section.name}` : ""
                                        }`,
                                    key: (opt) =>
                                        opt.section
                                            ? `${opt.store.id}_${opt.section.id}`
                                            : `${opt.store.id}`,
                                    renderOption: (opt, icon) => (
                                        <div>
                                            <div className="grow">
                                                <span className="bold-highlight">
                                                    {opt.store.name}
                                                </span>
                                                {opt.section ? <> - {opt.section.name}</> : null}
                                            </div>
                                            {icon}
                                        </div>
                                    ),
                                }}
                                isLoading={isLocationsOptionsLoading}
                                autocomplete
                                virtualize
                            />
                        )}
                    />
                </div>

                <div className={"mt-8 grid grid-cols-2 gap-2"}>
                    <BaseButton
                        buttonWidth="100%"
                        text={t("buttons.cancel")}
                        size="md"
                        type="button"
                        primaryOutlined
                        onClick={onClose}
                    />
                    <BaseButton
                        buttonWidth="100%"
                        text={t("buttons.import")}
                        size="md"
                        type="submit"
                        loading={isImportInProgress}
                    />
                </div>
            </form>
        </BaseModal>
    );
}
